import { isSameDay } from "date-fns"
import { z } from "zod"
import { MembershipStatusEnum } from "../../types/memberships"

export type NewHourReportData = z.infer<typeof NewHourReportDataSchema>
export type HourReportFormOptionsResponse = z.infer<typeof HourReportFormOptionsResponseSchema>
export type HourReportTaskOptionsResponse = z.infer<typeof HourReportTaskOptionsResponseSchema>
export type HourReportFormProjectOption = z.infer<typeof ProjectOptionSchema>
export type HourReportFormTaskOption = z.infer<typeof TaskOptionSchema>
export type HourReportFormMembershipOption = z.infer<typeof MembershipOptionSchema>
export type HourReportDayAuditResponse = z.infer<typeof HourReportDayAuditResponseSchema>
export type HourReportDayAuditsResponse = z.infer<typeof HourReportDayAuditsResponseSchema>
export type ReportedHourResponse = z.infer<typeof ReportedHourResponseSchema>
export type CreateHourReportResponse = z.infer<typeof CreateHourReportResponseSchema>
export type ProjectDetails = z.infer<typeof ProjectDetailsSchema>
export type HourReportRole = z.infer<typeof HourReportRoleSchema>
export const HourReportStatusSchema = z.enum(["open", "submitted", "checked", "approved"])
export type HourReportStatus = z.infer<typeof HourReportStatusSchema>

export type TaskDetails = {
  id: string
  title: string
  taskNumber: string
  parentTaskId: string | null
  ancestors: Omit<TaskDetails, "ancestors">[]
}

const ProjectDetailsSchema = z.object({
  id: z.string(),
  title: z.string(),
  projectNumber: z.string(),
  customer: z.string().optional(),
})

const TaskDetailsBaseSchema = z.object({
  id: z.string(),
  title: z.string(),
  taskNumber: z.string(),
  parentTaskId: z.string().nullable(),
})

const TaskDetailsSchema: z.ZodType<TaskDetails> = z.lazy(() => {
  return TaskDetailsBaseSchema.extend({
    ancestors: z.array(TaskDetailsBaseSchema),
  })
})

export const NewManualHourReportDataSchema = z.object({
  date: z.string(),
  projectId: z.string().nullable().optional(),
  taskId: z.string().nullable().optional(),
  method: z.literal("manual"),
  hours: z.number().min(0),
  description: z.string().optional(),
  status: HourReportStatusSchema.extract(["open", "submitted"]),
  membershipId: z.string().nullable(),
})

export const NewIntervalHourReportDataSchema = z.object({
  date: z.string(),
  projectId: z.string().nullable().optional(),
  taskId: z.string().nullable().optional(),
  method: z.literal("interval"),
  startTime: z.string(),
  endTime: z.string(),
  description: z.string().optional(),
  status: HourReportStatusSchema.extract(["open", "submitted"]),
  membershipId: z.string().nullable(),
})

export const NewTimerHourReportDataSchema = z.object({
  date: z.string(),
  projectId: z.string().nullable().optional(),
  taskId: z.string().nullable().optional(),
  method: z.literal("timer"),
  startTime: z.string(),
  endTime: z.string(),
  hours: z.number(),
  description: z.string().optional(),
  status: HourReportStatusSchema.extract(["open", "submitted"]),
  membershipId: z.string().nullable(),
})

export const ensureWithinSameDay = (data: any) => {
  if (data.method !== "timer") return true
  if (!data.timerStartTime) return false
  data.timerEndTime = data.timerEndTime || data.timerStartTime
  const startDate = new Date(data.timerStartTime)
  const endDate = new Date(data.timerEndTime)
  return isSameDay(startDate, endDate)
}

export const NewHourReportDataSchema = z
  .discriminatedUnion("method", [
    NewManualHourReportDataSchema,
    NewIntervalHourReportDataSchema,
    NewTimerHourReportDataSchema,
  ])
  .refine(ensureWithinSameDay, {
    message: "Start and end times must be within the same day",
  })

const HourReportRoleSchema = z.enum(["reporter", "checker", "approver", "hour-report-admin"])
const ReportedHourEditableAsSchema = HourReportRoleSchema.nullable()

export const ReportedHourResponseSchema = z.object({
  id: z.string(),
  date: z.string(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  hours: z.number(),
  description: z.string().nullable(),
  projectId: z.string().nullable(),
  project: ProjectDetailsSchema.nullable(),
  taskId: z.string().nullable(),
  task: TaskDetailsSchema.nullable(),
  membershipId: z.string().nullable(),
  memberFullName: z.string().nullable(),
  reporterUserId: z.string(),
  reporterUserFullName: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  status: HourReportStatusSchema,
  method: z.string(),
  reportedBy: z.string().nullable(),
  submittedBy: z.string().nullable(),
  submittedAt: z.string().nullable(),
  checkedBy: z.string().nullable(),
  checkedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  canMarkReadyForCheck: z.boolean(),
  canMarkReadyForApproval: z.boolean(),
  canMarkApproved: z.boolean(),
  editableAs: ReportedHourEditableAsSchema,
})

const ProjectOptionSchema = z.object({
  id: z.string(),
  title: z.string(),
  maintainerId: z.string(),
  maintainerType: z.string(),
  createdAt: z.string(),
  plannedStartDate: z.string().nullable(),
  plannedEndDate: z.string().nullable(),
})

const TaskOptionSchema = z.object({
  id: z.string(),
  title: z.string(),
  parentTaskId: z.string().nullable(),
  projectId: z.string(),
  projectTitle: z.string(),
  projectNumber: z.string(),
  maintainerId: z.string(),
  maintainerType: z.string(),
  level: z.number().min(0),
  order: z.number().min(0),
  fullTreeOrderPath: z.array(z.number()),
  taskNumber: z.string(),
  ancestors: z.array(z.object({ label: z.string() })),
})

const MembershipOptionSchema = z.object({
  id: z.string(),
  userFullname: z.string(),
  orgId: z.string(),
  status: z.enum([MembershipStatusEnum.ACTIVE, MembershipStatusEnum.IN_ACTIVE]),
})

export const HourReportFormOptionsResponseSchema = z.object({
  projects: z.array(ProjectOptionSchema),
  memberships: z.array(MembershipOptionSchema),
})

export const HourReportTaskOptionsResponseSchema = z.object({
  tasks: z.array(TaskOptionSchema),
})

export const HourReportDayAuditResponseSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  membershipId: z.string(),
  date: z.string(),
  submittedBy: z.string().nullable(),
  submittedAt: z.string().nullable(),
  checkedBy: z.string().nullable(),
  checkedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.string().nullable(),
  status: HourReportStatusSchema,
  canMarkReadyForCheck: z.boolean(),
  canMarkReadyForApproval: z.boolean(),
  canMarkApproved: z.boolean(),
  canManageAsHourReportAdmin: z.boolean(),
})

export const HourReportDayAuditsResponseSchema = z.object({
  audits: z.array(HourReportDayAuditResponseSchema),
})

export const CreateHourReportResponseSchema = z.object({ id: z.string() })
